/**
 * Utility functions for vote handling and formatting
 * Extracted from BillDetailPage.tsx for better maintainability
 */

/**
 * Get color class for vote display
 * 
 * @param vote - Vote value (Y/N/E/A/YES/NO/EXCUSED/ABSENT)
 * @returns Tailwind CSS class string for vote color
 */
export function getVoteColor(vote: string): string {
  switch (vote.toUpperCase()) {
    case 'Y':
    case 'YES':
      return 'text-success-600 dark:text-success-400 font-semibold';
    case 'N':
    case 'NO':
      return 'text-error-600 dark:text-error-400 font-semibold';
    case 'E':
    case 'EXCUSED':
      return 'text-warning-600 dark:text-warning-400 font-semibold';
    case 'A':
    case 'ABSENT':
      return 'text-text-secondary dark:text-text-dark-secondary font-semibold';
    default:
      return 'text-text-secondary dark:text-text-dark-secondary';
  }
}

/**
 * Format vote display text
 * 
 * @param vote - Vote value (Y/N/E/A)
 * @returns Human-readable vote text
 */
export function formatVoteDisplay(vote: string): string {
  switch (vote.toUpperCase()) {
    case 'Y':
      return 'Yes';
    case 'N':
      return 'No';
    case 'E':
      return 'Excused';
    case 'A':
      return 'Absent';
    default:
      return vote;
  }
}

/**
 * Calculate vote percentages
 * 
 * @param aye - Number of yes votes
 * @param no - Number of no votes
 * @param excused - Number of excused votes
 * @param absent - Number of absent votes
 * @returns Object with vote percentages
 */
export function calculateVotePercentages(
  aye: number,
  no: number,
  excused: number,
  absent: number
): { ayePercent: number; noPercent: number; totalVotes: number } {
  const totalVotes = aye + no + excused + absent;
  const ayePercent = totalVotes > 0 ? Math.round((aye / totalVotes) * 100) : 0;
  const noPercent = totalVotes > 0 ? Math.round((no / totalVotes) * 100) : 0;
  
  return { ayePercent, noPercent, totalVotes };
}

/**
 * Calculate vote breakdown from member votes
 * 
 * @param memberVotes - Array of member vote objects
 * @returns Vote breakdown object
 */
export function calculateVoteBreakdown(
  memberVotes: Array<{ vote: string }>
): { aye: number; no: number; excused: number; absent: number } {
  return memberVotes.reduce(
    (acc, memberVote) => {
      const vote = memberVote.vote?.toUpperCase();
      if (vote === 'Y' || vote === 'YES') acc.aye++;
      else if (vote === 'N' || vote === 'NO') acc.no++;
      else if (vote === 'E' || vote === 'EXCUSED') acc.excused++;
      else if (vote === 'A' || vote === 'V' || vote === 'ABSENT') acc.absent++;
      return acc;
    },
    { aye: 0, no: 0, excused: 0, absent: 0 }
  );
}

/**
 * Get vote type display properties
 * 
 * @param voteType - Type of vote (committee/final_passage/amendment/etc.)
 * @returns Object with color and label for display
 */
export function getVoteTypeDisplay(voteType?: string): { color: string; label: string } {
  switch (voteType) {
    case 'committee':
      return { color: '#8B5A2B', label: 'Committee Vote' };
    case 'final_passage':
      return { color: '#DC2626', label: 'Final Passage' };
    case 'amendment':
      return { color: '#7C3AED', label: 'Amendment Vote' };
    case 'second_reading':
      return { color: '#6B7280', label: 'Second Reading' };
    case 'first_reading':
      return { color: '#6B7280', label: 'First Reading' };
    default:
      return { color: '#6B7280', label: 'Other Vote' };
  }
}

/**
 * Check if vote breakdown has any votes
 * 
 * @param breakdown - Vote breakdown object
 * @returns True if there are any votes recorded
 */
export function hasVotes(breakdown: { aye: number; no: number; excused: number; absent: number }): boolean {
  return breakdown.aye > 0 || breakdown.no > 0 || breakdown.excused > 0 || breakdown.absent > 0;
}

